import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { ThirdPartyDraggable } from '@fullcalendar/interaction';
import { AlertController, ModalController } from '@ionic/angular';
import { ApiService } from 'src/app/api/api.service';
import { GlobalService } from 'src/app/api/global.service';
import { NgxFileDropEntry, FileSystemFileEntry, FileSystemDirectoryEntry } from 'ngx-file-drop';
import { ExplorerComponent } from '../../components/files/explorer/explorer.component';
import { CreatePage as SurveyCreatePage } from '../../surveys/create/create.page';
import { CreatePage as ClientsCreatePage } from 'src/app/clients/create/create.page';
import { SelectPage as SurveySelectPage } from '../../surveys/select/select.page';
import { SetupPage } from 'src/app/esignature/setup/setup.page';
import { AddressBookComponent } from 'src/app/components/address-book/address-book.component';
import { EmailTypes } from '../EmailTypes';
import { SelectPage as TeamMemberSelectPage } from 'src/app/team-members/select/select.page';
import { EmailPreviewComponent } from 'src/app/components/email-preview/email-preview.component';


@Component({
  selector: 'app-create',
  templateUrl: './create.page.html',
  styleUrls: ['./create.page.scss'],
})
export class CreatePage implements OnInit {

  
  @ViewChild('bodyEditor') bodyEditor: any;
  private lastCursorIndex: number | null = null;

  public EmailTypes = EmailTypes;

  @Input() outlookPlugin = false;
  // Pass in a draft (as returned by GET email_drafts/{guid}, with its `data` JSON blob) to
  // pre-fill the compose form from it instead of starting blank.
  @Input() draft: any = null;
  // Set once a draft has been loaded or successfully saved, so subsequent Save Draft clicks
  // update that same row instead of creating a new one each time.
  current_draft_guid: string = '';
  all_clients_active = false;
  secure_email_data = {
    to_list: [],
    file_list: [],
    type: 1,
    subject: '',
    expire_days: 0,
    body: '',
    use_phone_auth: true,
    show_in_list: true,
    step_id: 0,
    data: null,
    surveys: [],
    created_surveys: [],
    selected_surveys: [],
    esignatures: [],
    auto_remind: true,
    use_firm_days_between: true,
    days_between_email_reminders: 3,
    use_real_signatures: false,
    specific_files: false,
    description: '',
    any_team_member: false,
    allowed_team_members: [] as any[],
    use_cert: true,
    // Question request: the question the client answers, and whether they must sign their answer
    question: '',
    require_signature: false,
    // Checklist request: the items the client checks off - { guid, text, description }
    checklist: [] as any[]
  }

  client_data: any;
  company_data: any;
  combined_data: any;
  clients_have_phones = true;

  to_list_companies: any[] = [];
  clients_roles: any[] = [];
  team_members: any[] = [];
  team_members_valid = true;
  calendar_setup_warnings: string[] = [];
  private calendarSetupCache: Record<string, boolean> = {};

  //Dropzone files
  files: NgxFileDropEntry[] = [];
  //Email templates
  email_templates: any = [];
  selected_template = '0';

  get hasTypeSpecificSettings(): boolean {
    return this.secure_email_data.type == EmailTypes.signature_request ||
      this.secure_email_data.type == EmailTypes.survey ||
      this.secure_email_data.type == EmailTypes.appointment_request ||
      this.secure_email_data.type == EmailTypes.question_request ||
      this.secure_email_data.type == EmailTypes.checklist_request;
  }

  // Mirrors the icon/label already used for this type in emailTypeOptions, so the card heading
  // matches what the user picked in the Email Type card above it.
  get typeSpecificSectionTitle(): string {
    if (this.secure_email_data.type == EmailTypes.signature_request) return 'Signature Settings';
    if (this.secure_email_data.type == EmailTypes.survey) return 'Form Request';
    if (this.secure_email_data.type == EmailTypes.appointment_request) return 'Appointment Settings';
    if (this.secure_email_data.type == EmailTypes.question_request) return 'Question Settings';
    if (this.secure_email_data.type == EmailTypes.checklist_request) return 'Checklist Items';
    return '';
  }

  get typeSpecificSectionIcon(): string {
    if (this.secure_email_data.type == EmailTypes.signature_request) return 'fa-pen-nib';
    if (this.secure_email_data.type == EmailTypes.survey) return 'fa-clipboard-list';
    if (this.secure_email_data.type == EmailTypes.appointment_request) return 'fa-calendar-plus';
    if (this.secure_email_data.type == EmailTypes.question_request) return 'fa-circle-question';
    if (this.secure_email_data.type == EmailTypes.checklist_request) return 'fa-list-check';
    return 'fa-gear';
  }

  constructor(private apiService: ApiService, private alertController: AlertController, public globalService: GlobalService, private modalController: ModalController) {

  }

  ngOnInit() {
    this.secure_email_data.type = 1;
    this.secure_email_data.expire_days = 0;
    this.GetCompaniesAndClients();
    this.GetClients();
    this.GetCompanies();
    this.GetEmailTemplates();
    this.GetClientsRoles();
    this.GetTeamMembers();
    const currentUser = this.globalService.GetCurrentUser();
    if (currentUser) this.secure_email_data.allowed_team_members = [currentUser];

    // A loaded draft already carries its own body (including whatever signature was in it when
    // saved) - only pull the current signature default when starting a blank compose.
    if (this.draft) this.LoadDraft(this.draft);
    else this.GetEmailSignature();
  }

  // Restores the compose form from a saved draft - see SaveDraft for the matching snapshot shape.
  LoadDraft(draft: any) {
    try {
      const snapshot = JSON.parse(draft.data);
      if (snapshot.secure_email_data) Object.assign(this.secure_email_data, snapshot.secure_email_data);
      if (snapshot.to_list_companies) this.to_list_companies = snapshot.to_list_companies;
      this.current_draft_guid = draft.guid;
      this.CheckUserPhoneNumbers();
      this.ClientsListChanged();
    } catch (e) {
      console.error('Failed to load draft', e);
      this.globalService.presentToastMessage('Failed to load draft', false);
    }
  }

  // Saves (or, once current_draft_guid is set, updates in place) the entire compose form state as
  // one JSON blob - to_list_companies lives outside secure_email_data on this page, so it's bundled
  // alongside it here to fully restore the form later via LoadDraft.
  async SaveDraft() {
    const snapshot = {
      secure_email_data: this.secure_email_data,
      to_list_companies: this.to_list_companies
    };
    const payload = {
      subject: this.secure_email_data.subject || '(No Subject)',
      data: JSON.stringify(snapshot)
    };
    let result: any;
    if (this.current_draft_guid) {
      result = await this.apiService.put(`email_drafts/${this.current_draft_guid}`, payload);
    } else {
      result = await this.apiService.post('email_drafts', payload);
      if (result.success && result.data && result.data[0]) {
        this.current_draft_guid = result.data[0].guid;
      }
    }
    this.globalService.presentToastMessage(result.message, result.success);
    if (result.success) this.modalController.dismiss(null);
  }

  async GetEmailSignature() {
    let data: any = await this.apiService.get('team_members/email/signature');
    if (data.success) this.secure_email_data.body = data.data[0].signature;
  }
  async GetEmailTemplates() {
    let data: any = await this.apiService.get('email_templates');
    this.email_templates = data.data;
  }

  async GetClientsRoles() {
    let data: any = await this.apiService.get('firms/clients/roles');
    if (data.success) this.clients_roles = data.data || [];
  }

  async GetTeamMembers() {
    const data: any = await this.apiService.get('team_members');
    if (data.success) this.team_members = data.data || [];
  }

  RemoveTeamMember(tm: any) {
    this.secure_email_data.allowed_team_members =
      this.secure_email_data.allowed_team_members.filter((m: any) => m.guid !== tm.guid);
    this.CheckAppointmentPermissions();
    this.CheckCalendarSetups();
  }

  async presentSelectTeamMembers() {
    const modal = await this.modalController.create({
      component: TeamMemberSelectPage,
      cssClass: 'small-modal tall-modal secondary-modal',
      backdropDismiss: true,
      componentProps: {
        selecting_for_company: false,
        selecting_for_projects: false,
        selected_team_members: this.secure_email_data.allowed_team_members,
        remove_team_members: this.secure_email_data.allowed_team_members,
        in_team_members: [],
        target_header: 'Currently Selected'
      }
    });
    modal.onDidDismiss().then(() => {
      this.CheckAppointmentPermissions();
      this.CheckCalendarSetups();
    });
    return await modal.present();
  }

  OnAnyTeamMemberChange() {
    this.team_members_valid = true;
    this.team_members.forEach(tm => tm.permission_error = []);
    if (!this.secure_email_data.any_team_member) {
      const currentUser = this.globalService.GetCurrentUser();
      if (currentUser && !this.secure_email_data.allowed_team_members.some((m: any) => m.guid === currentUser.guid)) {
        this.secure_email_data.allowed_team_members = [{ ...currentUser }];
      }
      this.CheckAppointmentPermissions();
    }
    this.CheckCalendarSetups();
  }

  async CheckCalendarSetups() {
    if (this.secure_email_data.type != EmailTypes.appointment_request) {
      this.calendar_setup_warnings = [];
      return;
    }
    const tms_to_check = this.secure_email_data.any_team_member
      ? this.team_members
      : this.secure_email_data.allowed_team_members;

    const uncached = tms_to_check.filter(tm => !(tm.guid in this.calendarSetupCache));
    await Promise.all(uncached.map(async tm => {
      const data: any = await this.apiService.get(`calendar/setup/${tm.guid}`);
      this.calendarSetupCache[tm.guid] = data?.success && Array.isArray(data.data) && data.data.length > 0;
    }));

    const warnings: string[] = [];
    for (const tm of tms_to_check) {
      if (!this.calendarSetupCache[tm.guid]) {
        warnings.push(tm.display_name);
      }
    }
    this.calendar_setup_warnings = warnings;
  }

  async CheckAppointmentPermissions() {
    this.team_members_valid = true;
    this.team_members.forEach(tm => { tm.permission_error = []; tm.permissionCache = tm.permissionCache || {}; });

    const allClients = [...this.secure_email_data.to_list];
    const allCompanies = [...this.to_list_companies];
    if (!allClients.length && !allCompanies.length) return;

    const tms = this.secure_email_data.allowed_team_members.filter(tm => tm.is_admin !== 1);
    const fetches: Promise<void>[] = [];
    for (const tm of tms) {
      const cache = tm.permissionCache || (tm.permissionCache = {});
      for (const client of allClients) {
        const key = `${client.guid}_false`;
        if (!(key in cache)) {
          fetches.push(this.apiService.get(`team_members/${tm.guid}/permissions/${client.guid}/false`).then((r: any) => { cache[key] = r.success; }));
        }
      }
      for (const company of allCompanies) {
        const key = `${company.guid}_true`;
        if (!(key in cache)) {
          fetches.push(this.apiService.get(`team_members/${tm.guid}/permissions/${company.guid}/true`).then((r: any) => { cache[key] = r.success; }));
        }
      }
    }
    await Promise.all(fetches);

    for (const tm of tms) {
      const cache = tm.permissionCache;
      for (const client of allClients) {
        if (!cache[`${client.guid}_false`]) {
          this.team_members_valid = false;
          tm.permission_error.push(client.display_name || client.email || 'Unknown client');
        }
      }
      for (const company of allCompanies) {
        if (!cache[`${company.guid}_true`]) {
          this.team_members_valid = false;
          tm.permission_error.push(company.name || 'Unknown company');
        }
      }
    }
  }

  GetCompanyRoleName(company: any): string {
    if (!company.role_guid) return '';
    const role = this.clients_roles.find(r => r.guid === company.role_guid);
    return role ? role.title : '';
  }

  GetEffectiveDefaultClientGuidForCompany(company: any): string {
    if (!company.clients || !Array.isArray(company.clients)) {
      return company.default_client_guid || '';
    }
    if (company.role_guid) {
      const roleDefault = company.clients.find((c: any) => c.role_guid === company.role_guid && c.is_default_for_role);
      if (roleDefault) return roleDefault.guid;
    }
    return company.default_client_guid || '';
  }

  CompanyHasValidEmail(company: any): boolean {
    const guid = this.GetEffectiveDefaultClientGuidForCompany(company);
    if (!guid) return false;
    const client = this.client_data?.find((c: any) => c.guid === guid);
    return !!(client && client.email);
  }

  AnyRecipientMissingEmail(): boolean {
    for (const item of this.secure_email_data.to_list) {
      if (!item.email) return true;
    }
    for (const company of this.to_list_companies) {
      if (!this.CompanyHasValidEmail(company)) return true;
    }
    return false;
  }

  // ── To field typeahead ──────────────────────────────────────────────────

  to_search_query = '';
  to_suggestions: any[] = [];
  to_suggestions_active_index = -1;
  show_to_suggestions = false;
  // Only true once they've clicked/tabbed away from the field with unselected text left in it -
  // keeps the "Not added yet" warning from flashing while they're still actively typing.
  to_search_blurred_with_text = false;

  OnToSearchChange() {
    this.to_search_blurred_with_text = false;
    const q = (this.to_search_query || '').trim().toLowerCase();
    this.to_suggestions_active_index = -1;
    if (!q || !this.combined_data) {
      this.to_suggestions = [];
      this.show_to_suggestions = false;
      return;
    }
    const selected_contact_guids = new Set(this.secure_email_data.to_list.map((c: any) => c.guid));
    const selected_company_guids = new Set(this.to_list_companies.map((c: any) => c.guid));
    this.to_suggestions = this.combined_data.filter((item: any) => {
      if (item.is_company) {
        if (selected_company_guids.has(item.guid)) return false;
        return (item.name || '').toLowerCase().includes(q);
      } else {
        if (selected_contact_guids.has(item.guid)) return false;
        return (item.display_name || '').toLowerCase().includes(q) || (item.email || '').toLowerCase().includes(q);
      }
    }).slice(0, 15);
    this.show_to_suggestions = this.to_suggestions.length > 0;
  }

  OnToInputFocus() {
    this.to_search_blurred_with_text = false;
    if (this.to_search_query) this.OnToSearchChange();
  }

  OnToInputBlur() {
    // Selecting a suggestion is handled on (mousedown), which fires before this blur - so it's
    // always safe to just close the dropdown here.
    this.show_to_suggestions = false;
    // Leftover unselected text (if any) is surfaced by the "Not added yet" warning under the
    // field, but only once they've actually left the field - not while still typing.
    this.to_search_blurred_with_text = !!this.to_search_query?.trim();
  }

  OnToInputKeydown(event: KeyboardEvent) {
    if (!this.show_to_suggestions || this.to_suggestions.length === 0) {
      if (event.key === 'Enter' && this.to_search_query?.trim()) {
        event.preventDefault();
        this.AddEmailToList(this.to_search_query.trim(), null);
      }
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.to_suggestions_active_index = Math.min(this.to_suggestions_active_index + 1, this.to_suggestions.length - 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.to_suggestions_active_index = Math.max(this.to_suggestions_active_index - 1, 0);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const index = this.to_suggestions_active_index >= 0 ? this.to_suggestions_active_index : 0;
      this.SelectToSuggestion(this.to_suggestions[index]);
    } else if (event.key === 'Escape') {
      this.show_to_suggestions = false;
    }
  }

  SelectToSuggestion(item: any) {
    if (item.is_company) {
      this.to_list_companies.push(item);
    } else {
      this.secure_email_data.to_list.push(item);
    }
    this.to_search_query = '';
    this.to_suggestions = [];
    this.to_suggestions_active_index = -1;
    this.show_to_suggestions = false;
    this.to_search_blurred_with_text = false;
    this.CheckUserPhoneNumbers();
    this.ClientsListChanged();
  }

  // company.default_client is populated in GetCompaniesAndClients() from default_client_guid -
  // this is a plain lookup rather than GetEffectiveDefaultClientGuidForCompany's role-aware version
  // since no role has been picked yet at suggestion time.
  GetCompanyDefaultContactLabel(company: any): string {
    const client = company.default_client;
    if (!client) return 'No default contact';
    return `${client.display_name} (${client.email || 'No Email'})`;
  }

  async OpenAddressBook() {
    const modal = await this.modalController.create({
      component: AddressBookComponent,
      cssClass: 'large-modal',
      backdropDismiss: false,
      componentProps: {
        external_selection_mode: true,
        allow_client_selection: true,
        allow_non_client_selection: true,
        allow_company_selection: true,
        allow_contact_selection: true,
        allow_company_group_selection: false,
        allow_contact_group_selection: false,
        in_selected_companies: this.to_list_companies,
        in_selected_contacts: this.secure_email_data.to_list,
        expanded_clients: true
      },
    });
    modal.onDidDismiss().then(async (data: any) => {
      // A contact or company may have been created inside the address book - reload the lists the quick
      // search (and the lookups below) work from, otherwise it wouldn't show up until this page is reopened.
      await this.GetCompaniesAndClients();
      if (data && data.data) {
        this.secure_email_data.to_list = [];
        this.to_list_companies = [];
        data.data.forEach(element => {
          if (element.is_company) {
            this.to_list_companies.push(element);
          } else if (element.is_contact) {
            this.secure_email_data.to_list.push(element);
          }
        });
        this.CheckUserPhoneNumbers();
        this.ClientsListChanged();
      }
    });
    return await modal.present();
  }

  async RemoveCompanyFromList(company: any) {
    this.globalService.RemoveFromArray(this.to_list_companies, company.guid);
    this.CheckUserPhoneNumbers();
    this.ClientsListChanged();
  }

  async GetCompaniesAndClients() {
    await this.GetClients();
    await this.GetCompanies();
    for (const client of this.client_data) {
      client.is_company = false;
    }
    for (const company of this.company_data) {
      company.is_company = true;
      company.default_client = this.client_data.find(client => client && client.guid === company.default_client_guid) || null;
    }
    this.combined_data = [...this.client_data, ...this.company_data];
    
  }
  async GetClients() {
    let data: any = await this.apiService.GetClients();;
    if (data.data) this.client_data = data.data;
    else this.client_data = [];
  }

  async GetCompanies() {
    let data: any = await this.apiService.GetCompanies();
    console.log("Companies Data:", data);
    if (data.data) this.company_data = data.data;
    else this.company_data = [];
  }

  CombinedData() {
    
  }

  //Add to the email list to send the secure emails too.
  //If its a guid = existing client
  //If its just an email address = try and find client
  //If no client, prompt to create the client
  async AddEmailToList(input_value, user_data) {
    console.log(input_value);
    var guid = '';
    var phone_number = '';
    if (!this.globalService.validateEmail(input_value)) { this.globalService.presentToastMessage('Invalid Email Address', false); return; }

    this.to_search_query = '';
    this.to_suggestions = [];
    this.show_to_suggestions = false;

    //Check if that user exists
    var index = this.client_data.findIndex(function (item, i) { return item.email === input_value });
    if (index >= 0) {
      user_data = this.client_data[index];
    }
    else {
      this.presentCreateClientAlert(input_value);
      return;
    }


    //Add the user to the email list if they do not exist
    var guid_index = this.secure_email_data.to_list.findIndex(function (it, i) { return it.guid === user_data.guid });
    if (guid_index && guid_index < 0) {
      this.secure_email_data.to_list.push(user_data);
      this.CheckUserPhoneNumbers();
      this.ClientsListChanged();
    } else {
      this.globalService.presentToastMessage('That user is already added to this email', false);
    }
  }

  async RemoveEmailFromList(item) {
    this.globalService.RemoveFromArray(this.secure_email_data.to_list, item.guid);
    this.CheckUserPhoneNumbers();
    this.ClientsListChanged();
  }

  async ClientsListChanged() {
    //We need to check each clients and see if they are active or not, if we have all active we can just send the email,
    //If we have some inactive we need to prompt the user to setup a password for the email
    let allActive = true;
    for (let user of this.secure_email_data.to_list) {
      const client = this.client_data.find(c => c.guid === user.guid);
      if (client && client.active === 0) {
        allActive = false;
        break;
      }
    }
    // Also check default contacts of selected companies
    for (let company of this.to_list_companies) {
      const defaultClient = company.default_client ||
        (company.clients && company.clients.find(c => c.guid === company.default_client_guid));
      if (defaultClient && defaultClient.active === 0) {
        allActive = false;
        break;
      }
    }
    this.all_clients_active = allActive;
    if (this.secure_email_data.type === EmailTypes.appointment_request &&
        !this.secure_email_data.any_team_member) {
      this.CheckAppointmentPermissions();
    }
  }

  //Checks if all users have phone numbers linked to their account
  async CheckUserPhoneNumbers() {
    // console.log("CHECK PHONE NUMBERS");
    // let clients_have_phones = true;
    // for (let i = 0; i < this.secure_email_data.to_list_vis.length; i++) {
    //   let user = this.secure_email_data.to_list_vis[i];
    //   console.log(`user phone number ${user.phone_number}`);
    //   if (!user.phone_number || user.phone_number == 0) clients_have_phones = false;
    // }
    // this.clients_have_phones = clients_have_phones;
    // if (!this.clients_have_phones) this.secure_email_data.use_phone_auth = false;
  }

  async Log(){
    console.log("SECURE EMAIL DATA", this.secure_email_data);
  }

  async OpenEmailPreview() {
    const modal = await this.modalController.create({
      component: EmailPreviewComponent,
      cssClass: 'large-modal',
      backdropDismiss: true,
      componentProps: {
        type: this.secure_email_data.type,
        subject: this.secure_email_data.subject || '',
        description: this.secure_email_data.description || '',
        body: this.secure_email_data.body || '',
        contact: this.secure_email_data.to_list[0] || null,
        company: this.to_list_companies[0] || null,
      }
    });
    return await modal.present();
  }

  async Close() {
    const isDirty = this.secure_email_data.to_list?.length > 0
      || this.secure_email_data.subject?.trim()
      || this.secure_email_data.body?.trim()
      || this.secure_email_data.file_list?.length > 0;
    if (isDirty) {
      const alert = await this.alertController.create({
        header: 'Are you sure?',
        message: 'You have unsaved changes. If you cancel now, your work will be lost.',
        buttons: [
          { text: 'Keep Editing', role: 'cancel' },
          { text: 'Save Draft & Exit', handler: () => this.SaveDraft() },
          { text: 'Discard', role: 'destructive', handler: () => this.modalController.dismiss(null) }
        ]
      });
      await alert.present();
    } else {
      this.modalController.dismiss(null);
    }
  }



  async PromptForPassword(name: string, email: string): Promise<{ password: string; hint: string } | null> {
    return new Promise(async (resolve) => {
      let resolved = false;
      const alert = await this.alertController.create({
        backdropDismiss: false,
        header: 'Set Email Password & Hint',
        message: `Set an email password and hint for <b>${name}</b> (${email}):`,
        inputs: [
          { name: 'password', type: 'password', placeholder: 'Password' },
          { name: 'confirm_password', type: 'password', placeholder: 'Confirm Password' },
          { name: 'hint', type: 'text', placeholder: 'Hint' }
        ],
        buttons: [
          {
            text: 'Set Password',
            handler: (data) => {
              if (data.password !== data.confirm_password) {
                this.globalService.presentToastMessage('Passwords do not match. Please try again.', false);
                return false;
              }
              resolved = true;
              resolve({ password: data.password, hint: data.hint });
              return true;
            }
          }
        ]
      });
      await alert.present();
      await alert.onDidDismiss();
      if (!resolved) resolve(null);
    });
  }

  async SendAndClose() {
    // Build combined to_list: direct contacts + company items (API resolves company contacts server-side)
    const combined_to_list: any[] = [...this.secure_email_data.to_list];
    for (const company of this.to_list_companies) {
      const effectiveGuid = this.GetEffectiveDefaultClientGuidForCompany(company);
      const defaultClient = this.client_data?.find((c: any) => c.guid === effectiveGuid);
      combined_to_list.push({
        guid: '',
        company_guid: company.guid,
        role_guid: company.role_guid || '',
        password: '',
        confirm_password: '',
        hint: '',
        _display_name: company.name + (company.role_guid ? ` (${this.GetCompanyRoleName(company)})` : ''),
        _default_client: defaultClient || null
      });
    }

    // Collect all passwords/hints for inactive recipients before sending any requests
    for (const recipient of combined_to_list) {
      // Direct contact
      if (!recipient.company_guid && recipient.active === 0) {
        const creds = await this.PromptForPassword(recipient.display_name || recipient.email, recipient.email);
        if (creds) {
          recipient.password = creds.password;
          recipient.hint = creds.hint;
        }
      }
      // Company recipient whose effective default contact is inactive
      if (recipient.company_guid && recipient._default_client && recipient._default_client.active === 0) {
        const dc = recipient._default_client;
        const creds = await this.PromptForPassword(
          `${recipient._display_name} — ${dc.display_name || dc.email}`,
          dc.email
        );
        if (creds) {
          recipient.password = creds.password;
          recipient.hint = creds.hint;
        }
      }
    }

    // Build surveys array before sending
    this.secure_email_data.surveys = [
      ...this.secure_email_data.selected_surveys.map(s => ({ ...s, from_template: true })),
      ...this.secure_email_data.created_surveys.map(s => ({ ...s, from_template: false }))
    ];
    console.log("SURVEYS", this.secure_email_data.surveys);
    this.secure_email_data.specific_files = false;

    await this.globalService.presentLoading('Preparing to send...', 60000);

    // Send one request per recipient, updating the loading message for each
    const results: any[] = [];
    for (let i = 0; i < combined_to_list.length; i++) {
      const recipient = combined_to_list[i];
      const displayName = recipient._display_name || recipient.display_name || recipient.email || 'recipient';
      await this.globalService.presentLoading(
        `Sending ${i + 1} of ${combined_to_list.length}: ${displayName}...`,
        60000
      );
      // Strip internal helper fields before posting
      const { _display_name, _default_client, ...recipientPayload } = recipient;
      const email_payload = { ...this.secure_email_data, to_list: [recipientPayload],
        // Only send the question / checklist with the type that uses them
        question: this.secure_email_data.type == EmailTypes.question_request ? this.secure_email_data.question : '',
        require_signature: this.secure_email_data.type == EmailTypes.question_request && this.secure_email_data.require_signature,
        checklist: this.secure_email_data.type == EmailTypes.checklist_request ? this.secure_email_data.checklist : [] };
      const result: any = await this.apiService.post('emails', email_payload);
      results.push(result);
    }

    this.globalService.dismissLoading();

    const allSuccess = results.every(r => r.success);
    const anySuccess = results.some(r => r.success);

    // Once every recipient send has gone out, this is no longer a draft - clean it up so it
    // doesn't linger in the drafts list. Best-effort: a failed delete here shouldn't block the
    // send from being reported as successful.
    if (allSuccess && this.current_draft_guid) {
      this.apiService.delete(`email_drafts/${this.current_draft_guid}`, null).catch(() => {});
    }

    if (allSuccess) {
      this.globalService.presentToastMessage(results[0].message, true);
      this.modalController.dismiss(results.map(r => r.data));
    } else {
      const failedMessages = results.filter(r => !r.success).map(r => r.message).join(', ');
      this.globalService.presentToastMessage(failedMessages || 'One or more emails failed to send.', false);
      if (anySuccess) {
        this.modalController.dismiss(results.filter(r => r.success).map(r => r.data));
      }
    }
  }

  async presentCreateClientAlert(email) {
    const alert = await this.alertController.create({
      backdropDismiss: false,
      header: 'Contact Not Found',
      message: `No contact found with the email "${email}". Would you like to create a new contact to send the email?`,
      buttons: [
        {
          text: 'No',
          role: 'cancel',
          handler: () => {
            return;
          }
        },
        {
          text: 'Yes',
          handler: () => {
            this.PresentCreateClientModal(email);
            alert.dismiss();
          }
        }
      ]
    });
    await alert.present();





  }

  async PresentCreateClientModal(email) {
    let new_client = {
      email: email,
      email_re: email
    }
    const modal = await this.modalController.create({
      component: ClientsCreatePage,
      cssClass: 'tall-modal secondary-modal',
      backdropDismiss: false,
      componentProps: {
        inc_client: new_client,
        simple_create: true
      },
    })
    modal.onDidDismiss().then((data: any) => {
      if (data.data) {
        this.client_data.push(data.data[0]);
        this.AddEmailToList(email, data.data[0]);
      }
    });
    return await modal.present()
  }



  //File DROPZONE
  async dropped(files: NgxFileDropEntry[]) {
    for (const droppedFile of files) {
      // Is it a file?
      if (droppedFile.fileEntry.isFile) {

        const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;

        await fileEntry.file(async (file: File) => {
          let file_guid = await this.apiService.UploadFile(file,'0',null);
          if(file_guid != null) {
            let push_data = {
              guid: file_guid,
              file_name: file.name,
            }
            this.secure_email_data.file_list.push(push_data);
          }
        });
      } else {
        this.globalService.presentToastMessage('Only files are allowed', false);
      }
    }
  }

  async RemoveFile(file) {
    if (file.was_selected) this.globalService.RemoveFromArray(this.secure_email_data.file_list, file.guid);
    else {
      let data: any = await this.apiService.delete(`files/${file.guid}`, null);
      this.globalService.presentToastMessage(data.message, data.success);
      if (data.success) {
        this.globalService.RemoveFromArray(this.secure_email_data.file_list, file.guid);
      }
    }

  }

  async OpenFileSelect() {
    const modal = await this.modalController.create({
      component: ExplorerComponent,
      componentProps: {
        file_select_mode: true,
        root_folder_guid: '0'
      },
      backdropDismiss: true,
      cssClass: 'large-modal'
    });
    modal.present();

    const { data, role } = await modal.onWillDismiss();
    if (data) {
      for (let i = 0; i < data.length; i++) {
        data[i].was_selected = true;
        this.secure_email_data.file_list.push(data[i]);
      }
    }
  }

  //Email template
  async EmailTemplateChange() {
    this.secure_email_data.body = '';
    var selected_template = this.email_templates.find(template => template.guid == this.selected_template);
    if (selected_template) this.secure_email_data.body = selected_template.body;

  }

  readonly emailTypeOptions = [
    {
      value: EmailTypes.email,
      label: 'Email',
      icon: 'fa-solid fa-envelope',
      description: 'Send a secure email. The recipient authenticates to unlock, read the message and download the files.',
      isClientAction: true,
    },
    {
      value: EmailTypes.document_request,
      label: 'Document Request',
      icon: 'fa-solid fa-file-arrow-up',
      description: 'Request the client to upload one or more documents. Files are stored securely in their portal.',
      isClientAction: true,
    },
    {
      value: EmailTypes.signature_request,
      label: 'Signature Request',
      icon: 'fa-solid fa-pen-nib',
      description: 'Send a PDF for the client to sign electronically. Supports both simple and digitally-certified signatures.',
      isClientAction: true,
    },
    {
      value: EmailTypes.approval_request,
      label: 'Approval Request',
      icon: 'fa-solid fa-thumbs-up',
      description: 'Request the client to approve or reject a document or decision. Their response is recorded with a simple signature.',
      isClientAction: true,
    },
    {
      value: EmailTypes.survey,
      label: 'Form Request',
      icon: 'fa-solid fa-clipboard-list',
      description: 'Send a custom form or survey for the client to fill out. Responses are saved to their record and can be downloaded as a PDF later.',
      isClientAction: true,
    },
    {
      value: EmailTypes.appointment_request,
      label: 'Appointment Request',
      icon: 'fa-solid fa-calendar-plus',
      description: 'Let the client book a time slot with a team member directly from the email.',
      isClientAction: true,
    },
    {
      value: EmailTypes.question_request,
      label: 'Question Request',
      icon: 'fa-solid fa-circle-question',
      description: 'Ask the client a question they answer securely, optionally with a signature.',
      isClientAction: true,
    },
    {
      value: EmailTypes.checklist_request,
      label: 'Checklist Request',
      icon: 'fa-solid fa-list-check',
      description: 'Send the client a checklist to work through - they check off each item, then mark it as completed.',
      isClientAction: true,
    },
  ];

  // ---- Checklist request items ----
  new_checklist_item = '';
  new_checklist_description = '';

  AddChecklistItem() {
    const text = (this.new_checklist_item || '').trim();
    if (!text) return;
    if (!Array.isArray(this.secure_email_data.checklist)) this.secure_email_data.checklist = [];
    const description = (this.new_checklist_description || '').trim();
    this.secure_email_data.checklist.push({
      guid: (typeof crypto !== 'undefined' && (crypto as any).randomUUID) ? (crypto as any).randomUUID() : Date.now().toString(36) + Math.random().toString(36).slice(2),
      text,
      ...(description ? { description } : {})
    });
    this.new_checklist_item = '';
    this.new_checklist_description = '';
  }

  RemoveChecklistItem(item: any) {
    const index = this.secure_email_data.checklist.indexOf(item);
    if (index !== -1) this.secure_email_data.checklist.splice(index, 1);
  }

  async EmailTypeChange() {
    console.log("EMAIL TYPE CHANGE", this.secure_email_data.type);
    this.secure_email_data.data = null;
    this.CheckCalendarSetups();
  }

  get mixedRecipientWarning(): string {
    if (this.secure_email_data.to_list.length > 0 && this.to_list_companies.length > 0) {
      return 'This email is being sent to both contacts and companies. When using a contact.* field, if the email is being sent to a company, it will pull from the default contact for that company. If using a company.* field and the email is being sent to a contact, it will not pull any data for that field.';
    }
    return '';
  }

  AppendToSubject(field: string) {
    const current = this.secure_email_data.subject || '';
    const prefix = current.length > 0 && current[current.length - 1] !== ' ' ? ' ' : '';
    this.secure_email_data.subject = current + prefix + field + ' ';
  }

  AppendToDescription(field: string) {
    const current = this.secure_email_data.description || '';
    const prefix = current.length > 0 && current[current.length - 1] !== ' ' ? ' ' : '';
    this.secure_email_data.description = current + prefix + field + ' ';
  }

  OnEditorSelectionChanged(event: any) {
    if (event.range !== null) {
      this.lastCursorIndex = event.range.index;
    }
  }

  OnEditorContentChanged(event: any) {
    const range = event.editor?.getSelection();
    if (range !== null) {
      this.lastCursorIndex = range?.index ?? this.lastCursorIndex;
    }
  }

  AppendToBody(field: string) {
    const quill = this.bodyEditor?.quillEditor;
    if (quill) {
      const index = this.lastCursorIndex ?? quill.getLength();
      const charBefore = index > 0 ? quill.getText(index - 1, 1) : ' ';
      const charAfter = quill.getText(index, 1);
      const prefix = charBefore !== ' ' ? ' ' : '';
      const suffix = charAfter !== ' ' && charAfter !== '.' && charAfter !== '\n' && charAfter !== '' ? ' ' : '';
      const textToInsert = prefix + field + suffix;
      quill.insertText(index, textToInsert, 'user');
      const newIndex = index + textToInsert.length;
      quill.setSelection(newIndex, 0);
      this.lastCursorIndex = newIndex;
      // Sync ngModel
      this.secure_email_data.body = quill.root.innerHTML;
    } else {
      this.secure_email_data.body = (this.secure_email_data.body || '') + field;
    }
  }

  async RemoveSurvey(survey) {
    this.globalService.RemoveFromArray(this.secure_email_data.created_surveys, survey.guid);
  }
  async RemoveSelectedSurvey(survey) {
    this.globalService.RemoveFromArray(this.secure_email_data.selected_surveys, survey.guid);
  }

  async OpenSurveyCreate() {
    const modal = await this.modalController.create({
      component: SurveyCreatePage,
      componentProps: {

      },
      backdropDismiss: true,
      cssClass: 'large-modal'
    });
    modal.present();

    const { data, role } = await modal.onWillDismiss();
    if (data) {
      console.log("Created Survey:", data);
      if (data != null) {
        if (this.secure_email_data.created_surveys == null) {
          this.secure_email_data.created_surveys = [];
        }
        this.secure_email_data.created_surveys.push(data);
      }
    }
  }

  async OpenSurveySelect() {
    const modal = await this.modalController.create({
      component: SurveySelectPage,
      componentProps: {
        remove_surveys: this.secure_email_data.selected_surveys,
        selected_surveys: this.secure_email_data.selected_surveys
      },
      backdropDismiss: true,
      cssClass: 'large-modal'
    });
    modal.present();

    const { data, role } = await modal.onWillDismiss();
    if (data) {
      console.log("Selected Surveys:", data);
      if (data != null) {
        if (this.secure_email_data.data == null || this.secure_email_data.data.selected_surveys == null) {
          this.secure_email_data.selected_surveys = [];
        }
        this.secure_email_data.selected_surveys = data;
      }
    }
  }

  async StartESignatureSetup() {
    let has_pdf = false;
    //Check if the user has any PDFS, if they dont give them an error
    this.secure_email_data.file_list.forEach(file => {
      if (file.file_name.includes(".pdf")) has_pdf = true;
    });
    if (has_pdf) this.OpenESignatureSetup();
  }

  async OpenESignatureSetup() {
    let pdf_files = [];
    this.secure_email_data.file_list.forEach(file => {
      if (file.file_name.includes(".pdf")) pdf_files.push(file);
    });
    let client_guid = '';
    let company_guid = '';
    let role_guid = '';
    if (this.secure_email_data.to_list.length > 0) {
      client_guid = (this.secure_email_data.to_list[0] as any).guid || '';
    } else if (this.to_list_companies.length > 0) {
      company_guid = this.to_list_companies[0].guid || '';
      role_guid = this.to_list_companies[0].role_guid || '';
    }
    const modal = await this.modalController.create({
      component: SetupPage,
      componentProps: {
        files: pdf_files,
        use_cert: this.secure_email_data.use_cert,
        client_guid,
        company_guid,
        role_guid,
      },
      backdropDismiss: true,
      cssClass: 'large-modal'
    });
    modal.present();

    const { data, role } = await modal.onWillDismiss();
    if (data) {
      this.secure_email_data.esignatures = data;
      this.secure_email_data.esignatures.forEach(esig => {
        esig.use_cert = this.secure_email_data.use_cert;
      });
      this.SendAndClose();
    }
  }


   ngDoCheck() {
    this.CheckIfFormIsValid();
  }

  has_error = false;
  error_message = '';
  CheckIfFormIsValid() {
    let messages: string[] = [];

    // Check for at least one recipient
    if (
      (!this.secure_email_data.to_list || this.secure_email_data.to_list.length === 0) &&
      (!this.to_list_companies || this.to_list_companies.length === 0)
    ) {
      messages.push('Please add at least one recipient.');
    }

    // Check all recipients have a valid email address
    if (this.AnyRecipientMissingEmail()) {
      messages.push('One or more recipients are missing an email address.');
    }

    // Check for subject
    if (
      !this.secure_email_data.subject ||
      this.secure_email_data.subject.trim() === ''
    ) {
      messages.push('Please enter a subject.');
    }

    // Signature Request - must have at least one PDF file and only one recipient
    if (this.secure_email_data.type == EmailTypes.signature_request) {
      const hasPdf = this.secure_email_data.file_list.some(
        file => file.file_name && file.file_name.toLowerCase().endsWith('.pdf')
      );
      if (!hasPdf) {
        messages.push('Signature requests must include at least one PDF file.');
      }
      const totalRecipients = this.secure_email_data.to_list.length + this.to_list_companies.length;
      if (totalRecipients > 1) {
        messages.push('Signature requests can only be sent to one recipient at a time.');
      }
    }

    // Survey Request - must have at least one survey
    if (this.secure_email_data.type == EmailTypes.survey) {
      if(this.secure_email_data.selected_surveys.length == 0 && this.secure_email_data.created_surveys.length == 0) {
        messages.push('Form requests must include at least one form.');
      }
    }

    // Question Request - must have a question
    if (this.secure_email_data.type == EmailTypes.question_request) {
      const questionText = (this.secure_email_data.question || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
      if (!questionText) messages.push('Please enter the question for the client.');
    }

    // Checklist Request - must have at least one item
    if (this.secure_email_data.type == EmailTypes.checklist_request) {
      if (!(this.secure_email_data.checklist || []).some((item: any) => (item?.text || '').trim())) {
        messages.push('Please add at least one checklist item.');
      }
    }

    // Appointment Request validations
    if (this.secure_email_data.type == EmailTypes.appointment_request) {
      if (!this.secure_email_data.any_team_member &&
          this.secure_email_data.allowed_team_members.length === 0) {
        messages.push('Please select at least one team member, or allow booking with any team member.');
      }
      if (!this.team_members_valid) {
        messages.push('One or more selected team members do not have permission to access the selected client(s).');
      }
    }
    this.error_message = messages.join('<br>');
    this.has_error = messages.length > 0;
  }

}
